/**
 * ErpHistoryTab — zakładka „Historia sync" ekranu Integracja ERP.
 *
 * Zbiera panele historii jobów sync (te same co rozwijane wiersze w IntegrationsTab)
 * w jednym miejscu — po jednym `SyncJobsHistoryPanel` per integracja. Świadomie NIE
 * przepisuje logiki historii — reużywa istniejący komponent z identycznym kontraktem
 * propsów. Zmiana czysto frontendowa, bez backendu.
 */

import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import { Spinner } from '@/components/Spinner';
import { api } from '@/lib/api';

import { type ErpIntegrationDto } from './IntegrationsTab';
import { SyncJobsHistoryPanel } from './SyncJobsHistoryPanel';

export function ErpHistoryTab({ integrationId }: { integrationId?: string } = {}) {
  const { t } = useTranslation();

  const listQ = useQuery({
    queryKey: ['erp-integrations', 'history'],
    queryFn: async () => {
      const res = await api.get<{ integrations: ErpIntegrationDto[] }>('/erp-integrations', {
        params: {},
      });
      return res.data.integrations;
    },
    // Tryb pojedynczej integracji (widok szczegółu master-detail) nie potrzebuje listy.
    enabled: !integrationId,
  });

  // Widok szczegółu: sam panel dla wskazanej integracji, bez listy i nagłówków per integracja.
  if (integrationId) {
    return <SyncJobsHistoryPanel integrationId={integrationId} />;
  }

  if (listQ.isLoading) {
    return (
      <div className="flex justify-center p-10">
        <Spinner size="lg" />
      </div>
    );
  }

  const integrations = (listQ.data ?? []).filter((it) => it.gcrecord == null);

  if (integrations.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-border-secondary p-10 text-center text-sm text-text-secondary">
        {t('tenant.settings.integrations.history.empty')}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {integrations.map((it) => (
        <section key={it.id}>
          <h3 className="mb-2 text-sm font-medium text-text-primary">{it.name}</h3>
          <SyncJobsHistoryPanel integrationId={it.id} />
        </section>
      ))}
    </div>
  );
}
